import { REWARD_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { REWARD_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { Address } from '@/global/types/common.type';
import { useGA, useWeb3 } from '@/hooks';
import { useWeb3Balance } from '@/providers';
import { useClaimReferralV1Mutation, useFetchReferralStatsQuery } from '@/store/services/referral.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import { ClaimReferralProps, ReferralV1Hook } from '../types/referral.type';

const useReferralV1 = (): ReferralV1Hook => {
  const { config, address } = useWeb3();
  const { trackButtonClickEvent } = useGA();
  const { getWalletBalance } = useWeb3Balance();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const [claimReferralV1] = useClaimReferralV1Mutation();
  const {
    isLoading: isLoadingReferralStats,
    data: referralStats,
    refetch: refetchReferralStats,
  } = useFetchReferralStatsQuery({ address: address as string, nft, page: 'referral' }, { skip: !address || !nft });

  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);

  const handleClaimReferral = useCallback(
    async ({ nodeId, onSuccess }: ClaimReferralProps) => {
      if (!address) return;
      if (!projectInfo?.referral.referralAddress) {
        toast.error('Referral contract not found');
        return;
      }
      setIsClaimingTrue();
      trackButtonClickEvent({ name: 'claim_rewards', address });
      try {
        const nftAddress = nodeId ?? nft;
        const claimRes = await claimReferralV1({ nft: nftAddress, address });
        if (
          !claimRes.data ||
          claimRes.data.tokens.length === 0 ||
          claimRes.data.amounts.length === 0 ||
          Object.keys(claimRes.data.signature).length === 0
        ) {
          toast.error('Claim referral failed. Please try again or contact to support');
          return;
        }
        const {
          tokens,
          amounts,
          deadline,
          nonce,
          signature: { v, s, r },
        } = claimRes.data;
        const hash = await writeContract(config, {
          abi: REWARD_ABI,
          address: (projectInfo.referral.referralAddress as Address) ?? REWARD_V1_SC_ADDRESS,
          functionName: 'claimAll',
          args: [nft, tokens, address, amounts, deadline, nonce, v, r, s],
        });
        toast(TransactionSubmittedToast());
        await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed rewards successfully');
        getWalletBalance();
        refetchReferralStats();
        onSuccess?.();
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleReferral');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim referral failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [
      address,
      config,
      nft,
      projectInfo,
      claimReferralV1,
      getWalletBalance,
      setIsClaimingFalse,
      setIsClaimingTrue,
      refetchReferralStats,
    ],
  );

  return {
    referralStats,
    isClaiming,
    isLoadingReferralStats,
    handleClaimReferral,
    refetchReferralStats,
  };
};

export { useReferralV1 };
